'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { isDeckId, isFileId, isWorkbookId } from 'pc-types';

import { recordClientAuditEvent } from '@/lib/audit-client';

/** Everything that can live in a tab: editors plus the read-only file viewer. */
export type EditorTabKind = 'deck' | 'workbook' | 'file';

export function isTabDocumentId(value: unknown): value is string {
  return isDeckId(value) || isWorkbookId(value) || isFileId(value);
}

export function tabKindFromId(id: string): EditorTabKind {
  if (isWorkbookId(id)) return 'workbook';
  if (isFileId(id)) return 'file';
  return 'deck';
}

export interface EditorTab {
  documentId: string;
  kind: EditorTabKind;
  lastActiveAt: number;
}

interface TabRegistryValue {
  tabs: EditorTab[];
  activeDocumentId: string | null;
  openTab: (documentId: string) => void;
  activateTab: (documentId: string) => void;
  closeTab: (documentId: string) => void;
  moveTab: (documentId: string, targetDocumentId: string) => void;
}

const MAX_OPEN_TABS = 6;
const STORAGE_KEY = 'pc-editor-tabs';

const TabRegistryContext = createContext<TabRegistryValue | null>(null);

export function editorTabHref(tab: Pick<EditorTab, 'documentId' | 'kind'>) {
  switch (tab.kind) {
    case 'workbook':
      return `/sheets/${tab.documentId}/edit`;
    case 'file':
      return `/files/${tab.documentId}/view`;
    default:
      return `/decks/${tab.documentId}/edit`;
  }
}

function loadPersistedTabs(): EditorTab[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Array<{
      documentId?: unknown;
      lastActiveAt?: unknown;
    }>;
    return parsed
      .filter((entry) => isTabDocumentId(entry.documentId))
      .map((entry, index) => ({
        documentId: entry.documentId as string,
        kind: tabKindFromId(entry.documentId as string),
        // Real recency when persisted; array (insertion) order is only the
        // fallback, or LRU eviction after a reload closes the wrong tab.
        lastActiveAt:
          typeof entry.lastActiveAt === 'number' &&
          Number.isFinite(entry.lastActiveAt)
            ? entry.lastActiveAt
            : index,
      }))
      .slice(0, MAX_OPEN_TABS);
  } catch {
    return [];
  }
}

export function TabRegistryProvider({ children }: { children: ReactNode }) {
  // Starts empty on BOTH server and client — localStorage restore happens
  // after mount, otherwise the SSR html and the first client render diverge
  // and React reports a hydration mismatch.
  const [tabs, setTabs] = useState<EditorTab[]>([]);
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const activeRef = useRef<string | null>(null);
  activeRef.current = activeDocumentId;
  const restoredRef = useRef(false);

  useEffect(() => {
    const persisted = loadPersistedTabs();
    restoredRef.current = true;
    if (persisted.length === 0) return;
    // The route's registrar effect may have opened the current document
    // before this restore ran (child effects fire first) — merge, keeping
    // persisted order first and never duplicating.
    setTabs((current) => {
      const merged = [
        ...persisted.filter(
          (tab) => !current.some((t) => t.documentId === tab.documentId)
        ),
        ...current,
      ];
      return merged.slice(-MAX_OPEN_TABS);
    });
  }, []);

  useEffect(() => {
    // Never persist before the restore ran — the initial empty render would
    // wipe the saved tab list.
    if (!restoredRef.current) return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          tabs.map((tab) => ({
            documentId: tab.documentId,
            lastActiveAt: tab.lastActiveAt,
          }))
        )
      );
    } catch {
      // Persistence is best-effort.
    }
  }, [tabs]);

  const openTab = useCallback((documentId: string) => {
    if (!isTabDocumentId(documentId)) return;
    setTabs((current) => {
      if (current.some((tab) => tab.documentId === documentId)) return current;
      const next = [
        ...current,
        {
          documentId,
          kind: tabKindFromId(documentId),
          lastActiveAt: Date.now(),
        },
      ];
      if (next.length <= MAX_OPEN_TABS) return next;
      // LRU-evict, never the active tab or the one just opened. Evicted
      // editors are server-persisted, so dropping the mount is safe.
      const evictable = next
        .filter(
          (tab) =>
            tab.documentId !== documentId &&
            tab.documentId !== activeRef.current
        )
        .sort((a, b) => a.lastActiveAt - b.lastActiveAt)[0];
      return evictable
        ? next.filter((tab) => tab !== evictable)
        : next.slice(next.length - MAX_OPEN_TABS);
    });
  }, []);

  const activateTab = useCallback((documentId: string) => {
    setActiveDocumentId(documentId);
    setTabs((current) =>
      current.map((tab) =>
        tab.documentId === documentId
          ? { ...tab, lastActiveAt: Date.now() }
          : tab
      )
    );
  }, []);

  const closeTab = useCallback((documentId: string) => {
    const kind = tabKindFromId(documentId);
    if (kind !== 'file') {
      recordClientAuditEvent({
        action: kind === 'workbook' ? 'sheet.closed' : 'deck.closed',
        resourceId: documentId,
        uiSource: {
          ariaLabel: 'Close tab',
          component: 'TabStrip',
          surface: 'editor-tabs',
        },
      });
    }
    setTabs((current) =>
      current.filter((tab) => tab.documentId !== documentId)
    );
  }, []);

  const moveTab = useCallback(
    (documentId: string, targetDocumentId: string) => {
      setTabs((current) => {
        const from = current.findIndex((tab) => tab.documentId === documentId);
        const to = current.findIndex(
          (tab) => tab.documentId === targetDocumentId
        );
        if (from < 0 || to < 0 || from === to) return current;
        const next = [...current];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
    },
    []
  );

  const value = useMemo(
    () => ({ tabs, activeDocumentId, openTab, activateTab, closeTab, moveTab }),
    [tabs, activeDocumentId, openTab, activateTab, closeTab, moveTab]
  );

  // Dev/e2e-only observability, matching window.__pcEditor/__pcSheets.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    (window as unknown as { __pcTabs?: unknown }).__pcTabs = value;
  }, [value]);

  return (
    <TabRegistryContext.Provider value={value}>
      {children}
    </TabRegistryContext.Provider>
  );
}

export function useTabRegistry(): TabRegistryValue {
  const value = useContext(TabRegistryContext);
  if (!value) {
    throw new Error('useTabRegistry must be used inside TabRegistryProvider');
  }
  return value;
}
